import React, { useEffect } from "react"
import { Link } from "react-router-dom"
import { Container } from "reactstrap"
import SoldxHeader from "../../../components/SoldxHeader"
import SoldxLandingFooter from "../../../components/SoldxLandingFooter"
import { SupportPhonePair } from "../../../components/SupportContactPhones"
import {
  SUPPORT_COMPANY_LEGAL_NAME,
  SUPPORT_CONTACT_INTRO,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO,
} from "../../../config/supportContact"
import "./TermsAndConditions.scss"

const TermsAndConditions = () => {
  useEffect(() => {
    document.title = "Terms and Conditions | SoldX"
  }, [])

  return (
    <div className="soldx-landing soldx-legal-page">
      <SoldxHeader solid />
      <main className="soldx-legal-main">
        <Container className="soldx-legal-container">
          <div className="soldx-legal-back-wrap">
            <Link to="/" className="soldx-legal-back">
              <i className="mdi mdi-arrow-left me-1" aria-hidden />
              Back to home
            </Link>
          </div>

          <div className="soldx-legal-card">
            <header className="soldx-legal-card-header">
              <h1 className="soldx-legal-card-title">Terms and Conditions</h1>
            </header>
            <div className="soldx-legal-card-body">
              <div className="soldx-legal-dates" aria-label="Document dates">
                <span className="soldx-legal-dates-item">
                  <strong>Effective Date:</strong> 01/09/2025
                </span>
                <span className="soldx-legal-dates-item soldx-legal-dates-item--end">
                  <strong>Last Updated:</strong> 19/09/2025
                </span>
              </div>

              <div className="soldx-legal-content">
                <section className="soldx-legal-section">
                  <h2>Introduction</h2>
                  <p>
                    Welcome to SoldX. These Terms and Conditions (&quot;Terms&quot;) govern your access to and use of the
                    SoldX platform, including the website, mobile applications, and related services. By using the
                    Platform, you agree to comply with and be legally bound by these Terms. If you do not agree, you may
                    not use the Platform.
                  </p>
                </section>

                <section className="soldx-legal-section">
                  <h2>Eligibility</h2>
                  <ul>
                    <li>Users must be 18 years or older and capable of entering into legally binding contracts under Indian law.</li>
                    <li>By registering, you confirm that all information provided is accurate, current, and complete.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Definitions</h2>
                  <ul>
                    <li>
                      <strong>Buyer:</strong> A registered user intending to purchase a property via auction or listing.
                    </li>
                    <li>
                      <strong>Seller:</strong> A property owner, builder, or developer listing properties for sale.
                    </li>
                    <li>
                      <strong>Broker:</strong> A licensed intermediary registered on the platform who facilitates transactions.
                    </li>
                    <li>
                      <strong>Auction:</strong> A live bidding event hosted on SoldX for property sales.
                    </li>
                    <li>
                      <strong>Listing:</strong> A property posted with details such as images, pricing, and status.
                    </li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>User Accounts</h2>
                  <ul>
                    <li>Users must create an account with valid contact details and identification.</li>
                    <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                    <li>SoldX reserves the right to suspend or terminate accounts for misuse, fraud, or breach of these Terms.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Auctions &amp; Bidding Rules</h2>
                  <ul>
                    <li>Participation in auctions requires pre-registration.</li>
                    <li>
                      The highest bid at auction close may result in a binding agreement, subject to seller confirmation and
                      due diligence.
                    </li>
                    <li>
                      Bidders must ensure financial capability before placing bids. Failure to honor winning bids may result
                      in forfeiture of deposits and account suspension.
                    </li>
                    <li>
                      Sellers retain the right to set reserve prices. If reserve price is not met, the seller may decline the
                      highest bid.
                    </li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Payments &amp; Fees</h2>
                  <ul>
                    <li>Registration on SoldX is free for buyers.</li>
                    <li>
                      Sellers and brokers may be subject to listing fees, success fees, or subscription charges as per
                      SoldX&apos;s pricing policies.
                    </li>
                    <li>Transaction-related payments, including deposits, must be made via SoldX-approved payment channels.</li>
                    <li>All payments are non-refundable unless explicitly stated.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Broker Regulations</h2>
                  <ul>
                    <li>Brokers must be RERA-registered (as applicable under state laws).</li>
                    <li>
                      SoldX provides analytics dashboards, but brokers remain responsible for compliance with real estate
                      regulations.
                    </li>
                    <li>Misrepresentation or non-disclosure of material facts will result in penalties and termination.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>User Obligations</h2>
                  <p>You agree not to:</p>
                  <ul>
                    <li>Post unlawful, fraudulent, or misleading content.</li>
                    <li>Use the platform for speculative, false, or fraudulent bidding.</li>
                    <li>Violate intellectual property rights of SoldX or third parties.</li>
                    <li>Attempt unauthorized access, reverse engineering, or tampering with the platform.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Legal Due Diligence &amp; Advisory Services</h2>
                  <ul>
                    <li>
                      Buyers are responsible for conducting title checks, RERA verification, and due diligence before
                      finalizing transactions.
                    </li>
                    <li>SoldX provides legal, financial, and tax advisory services separately on a paid basis.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Limitation of Liability</h2>
                  <p>SoldX acts only as a facilitator and is not responsible for disputes arising between buyers, sellers, or brokers.</p>
                  <p>SoldX shall not be liable for any:</p>
                  <ul>
                    <li>Loss of data, revenue, or profits.</li>
                    <li>Disputes regarding property title, condition, or pricing.</li>
                    <li>Third-party errors, misrepresentations, or fraud.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Intellectual Property</h2>
                  <ul>
                    <li>
                      All content, branding, software, and technology on SoldX are the intellectual property of SoldX India
                      Pvt Ltd.
                    </li>
                    <li>
                      Users are granted a limited, non-exclusive, non-transferable license to use the platform for
                      personal/business purposes only.
                    </li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Privacy &amp; Data Protection</h2>
                  <ul>
                    <li>User data is collected and processed in accordance with the SoldX Privacy Policy.</li>
                    <li>
                      By using SoldX, you consent to the collection and use of your personal information for verification,
                      analytics, and transaction facilitation.
                    </li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Termination</h2>
                  <ul>
                    <li>SoldX reserves the right to suspend or terminate user accounts for violations of these Terms.</li>
                    <li>Users may deactivate their accounts by written request.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Governing Law &amp; Jurisdiction</h2>
                  <ul>
                    <li>These Terms are governed by the laws of India.</li>
                    <li>Any disputes shall be subject to the exclusive jurisdiction of the courts of Haryana.</li>
                  </ul>
                </section>

                <section className="soldx-legal-section">
                  <h2>Amendments</h2>
                  <p>
                    SoldX reserves the right to update or amend these Terms at any time. Continued use of the platform after
                    changes indicates acceptance.
                  </p>
                </section>

                <section className="soldx-legal-section soldx-legal-contact">
                  <h2>Contact Us</h2>
                  <p>{SUPPORT_CONTACT_INTRO}</p>
                  <p className="soldx-legal-contact-block">
                    <strong>{SUPPORT_COMPANY_LEGAL_NAME}</strong>
                    <br />
                    Email:{" "}
                    <a href={SUPPORT_MAILTO} className="soldx-legal-link">
                      {SUPPORT_EMAIL}
                    </a>
                    <br />
                    Phone:{" "}
                    <SupportPhonePair linkClassName="soldx-legal-link" />
                  </p>
                </section>
              </div>
            </div>
          </div>
        </Container>
      </main>

      <SoldxLandingFooter />
    </div>
  )
}

export default TermsAndConditions
